import clsx from 'clsx';
import { cloneElement, ReactElement, ReactNode } from 'react';

import { Icon, IconProps } from '../icon/Icon';
import { TooltipProps } from '../tooltip/Tooltip';
import styles from './RadioGroup.module.scss';

export interface RadioGroupItem {
  /** Stable id; also used as the `<input>` value and `htmlFor` target. */
  id: string;
  /** Test selector. Defaults to `id`. */
  testId?: string;
  /** Visible label rendered next to the radio input. */
  label: ReactNode;
  /** Optional `<Tooltip>` element to wrap the label in. */
  tooltip?: ReactElement<Omit<TooltipProps, 'children'>>;
  /** When true, applies the error visual treatment (red ring) to this item. */
  error?: boolean;
  /** Optional icon rendered inside the label, on the leading edge. Receives full `IconProps`. */
  icon?: IconProps;
  /** When true, the item is rendered as disabled and ignores clicks. */
  disabled?: boolean;
  /** When true, replaces the radio with a lock icon and renders the muted treatment. */
  locked?: boolean;
}

export interface RadioGroupProps {
  /** Selectable items in the group. */
  items: RadioGroupItem[];
  /** Id of the currently-selected item (controlled). */
  activeId?: string;
  /** Layout direction. Defaults to `'column'`. */
  direction?: 'column' | 'row';
  /** Size preset. Defaults to `'medium'`. */
  size?: 'medium' | 'small';
  /** Optional heading rendered above the group. */
  groupLabel?: string;
  /** Extra class forwarded to the wrapper. */
  className?: string;
  /** Called when an item is clicked. Receives `(id, index)`. */
  onClick?: (id: string, idx: number) => void;
  /** Called on the native input `change`. Receives `(id, index)`. */
  onChange?: (id: string, idx: number) => void;
}

/**
 * Mutually-exclusive selection group: a list of radio inputs where
 * exactly one item can be active at a time.
 *
 * Items support per-row icons, tooltips, and error / disabled / locked
 * states. Pair `activeId` with `onChange` (or `onClick`) to control
 * selection from the parent. For two-or-three-option toggles where a
 * segmented look fits better, use `ButtonGroup` instead.
 */
export const RadioGroup = ({
  className,
  items,
  activeId,
  groupLabel,
  onChange,
  onClick,
  direction = 'column',
  size = 'medium',
}: RadioGroupProps) => {
  return (
    <div className={clsx(styles.container, className)} data-radio-container>
      {groupLabel && <span className={styles.groupLabel}>{groupLabel}</span>}
      <div
        className={
          direction === 'column' ? styles.groupColumn : styles.groupRow
        }
        role="radiogroup"
      >
        {items.map((item, idx) => {
          const isLocked = !!item.locked;
          const isDisabled = !!item.disabled;
          const isError = !!item.error;
          const isInactive = isDisabled || isLocked;

          const TooltipWithLabel = item.tooltip
            ? cloneElement(
                item.tooltip,
                {
                  placement: item.tooltip.props.placement ?? 'top-start',
                  inPortal: true,
                  ...item.tooltip.props,
                },
                <span className={clsx(styles.label, styles.labelWithTooltip)}>
                  {item.label}
                </span>
              )
            : null;

          const testId = item.testId ?? item.id;

          return (
            <label
              key={item.id}
              className={clsx(
                styles.radioLabel,
                size === 'medium'
                  ? styles.radioLabelMedium
                  : styles.radioLabelSmall,
                {
                  [styles.radioLabelDisabled]: isDisabled,
                  [styles.radioLabelInactive]: isInactive,
                  [styles.radioLabelEnabled]: !isDisabled,
                  [styles.radioLabelLocked]: isLocked,
                }
              )}
              data-testid={testId}
              data-radio-label
              htmlFor={item.id}
            >
              {isLocked && (
                <Icon
                  className={styles.lockIcon}
                  iconName="LockLight"
                  size={20}
                />
              )}
              <input
                type="radio"
                className={clsx(styles.radioInput, {
                  [styles.radioInputDisabled]: isInactive,
                  [styles.radioInputError]: !isInactive && isError,
                  [styles.radioInputLocked]: isLocked,
                  [styles.radioInputUnlocked]: !isLocked,
                })}
                data-testid={`radio-${testId}`}
                data-radio-input
                id={item.id}
                value={item.id}
                disabled={isDisabled}
                onChange={() => onChange?.(item.id, idx)}
                onClick={onClick ? () => onClick(item.id, idx) : undefined}
                checked={item.id === activeId}
              />
              <div className={styles.labelContainer}>
                {item.icon && (
                  <Icon className={styles.styledIcon} {...item.icon} />
                )}
                {TooltipWithLabel ?? (
                  <span className={styles.label}>{item.label}</span>
                )}
              </div>
            </label>
          );
        })}
      </div>
    </div>
  );
};
